import styled, { useTheme } from 'styled-components'

import { IconLeft } from 'components/Icons/IconLeft'
import { IconRight } from 'components/Icons/IconRight'

const PaginationContainer = styled.div`
  margin-top: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
`

const PageButton = styled.div<{ $isActive?: boolean; $disabled?: boolean }>`
  display: flex;
  margin: 0 2px;
  padding: 5px 10px;
  background-color: ${p => (p.$isActive ? p.theme.colors.background.hover : p.theme.colors.background.primary)};
  cursor: pointer;
  font-size: ${p => p.theme.typography.size.Body}px;
  font-weight: ${p => p.theme.typography.weight.SemiBold};
  border-radius: 4px;

  &:hover {
    background-color: ${p => p.theme.colors.background.hover};
  }

  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    margin: 0;
    padding: 4px 8px;
  }

  ${p => p.$disabled && `pointer-events: none;`}
`

interface PaginationProps {
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
  type: string
}

export function paginate<T>(array: T[], pageSize: number, currentPage: number): T[] {
  const start = (currentPage - 1) * pageSize
  const end = start + pageSize
  return array.slice(start, end)
}

const Pagination = ({ type, currentPage, totalPages, onPageChange }: PaginationProps) => {
  const handlePrevious = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1)
    }
  }

  const handleNext = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1)
    }
  }

  const handlePageClick = (page: number) => {
    onPageChange(page)
  }

  const renderPageNumbers = () => {
    const pageNumbers = []
    const siblings = 1
    const totalNumbersToShow = siblings * 2 + 3
    const totalBlocks = totalNumbersToShow + 2

    if (totalPages > totalBlocks) {
      const startPage = Math.max(2, currentPage - siblings)
      const endPage = Math.min(totalPages - 1, currentPage + siblings)

      pageNumbers.push(
        <PageButton key={`${type}-1`} onClick={() => handlePageClick(1)} $isActive={currentPage === 1}>
          1
        </PageButton>
      )

      if (startPage > 2) {
        pageNumbers.push(<span key={`${type}-left-ellipsis`}>...</span>)
      }

      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(
          <PageButton key={`${type}-${i}`} onClick={() => handlePageClick(i)} $isActive={currentPage === i}>
            {i}
          </PageButton>
        )
      }

      if (endPage < totalPages - 1) {
        pageNumbers.push(<span key={`${type}-right-ellipsis`}>...</span>)
      }

      pageNumbers.push(
        <PageButton
          key={`${type}-${totalPages}`}
          onClick={() => handlePageClick(totalPages)}
          $isActive={currentPage === totalPages}
        >
          {totalPages}
        </PageButton>
      )
    } else {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(
          <PageButton key={`${type}-${i}`} onClick={() => handlePageClick(i)} $isActive={currentPage === i}>
            {i}
          </PageButton>
        )
      }
    }

    return pageNumbers
  }

  const theme = useTheme()

  return (
    <PaginationContainer>
      <PageButton onClick={handlePrevious} $disabled={currentPage === 1 || totalPages === 0}>
        <IconLeft stroke={currentPage === 1 || totalPages === 0 ? theme.colors.text.disabled : ''} />
      </PageButton>
      {renderPageNumbers()}
      <PageButton onClick={handleNext} $disabled={currentPage === totalPages || totalPages === 0}>
        <IconRight stroke={currentPage === totalPages || totalPages === 0 ? theme.colors.text.disabled : ''} />
      </PageButton>
    </PaginationContainer>
  )
}

export default Pagination
